import * as React from 'react';
import { FunctionComponent, useMemo, useState } from 'react';
import propz from 'propz';
import { HelpIconButton } from '../../HelpIconButton/HelpIconButton';
import { getEmailOrMisEmail, getUserRole } from 'Src/helpers/accessor/accessor';
import { GeneralMessageRecipientChannelSummary } from 'Src/helpers/service/admin/generalMessage';

interface Props {
  affectedUsers?: any[];
  affectedUsersCount?: number;
  channelSummary?: GeneralMessageRecipientChannelSummary;
  isLoading?: boolean;
  isEmailExist: boolean;
  isPushExist: boolean;
  isSmsExist: boolean;
  isLoadingAffectedUserList?: boolean;
  onLoadAffectedUsers?: () => void;
  onHelpClick?: () => void;
  isModern?: boolean;
  hasChildVariables?: boolean;
}

type RecipientChannelFilter = 'all' | 'email' | 'push' | 'sms' | 'none';

const EMPTY_CHANNEL_SUMMARY: GeneralMessageRecipientChannelSummary = {
  total: 0,
  email: 0,
  push: 0,
  sms: 0,
  noChannels: 0
};

const hasChannel = (user: any, channel: 'Email' | 'Push' | 'Sms'): boolean =>
  propz.get(user, ['channels', `is${channel}ChannelsExist`], false);

const getName = (user: any): string =>
  `${propz.get(user, ['firstName'], '')} ${propz.get(user, ['lastName'], '')}`.trim() || 'Unnamed recipient';

const getInitials = (user: any): string =>
  [propz.get(user, ['firstName'], ''), propz.get(user, ['lastName'], '')]
    .filter(Boolean)
    .map(value =>
      String(value)
        .charAt(0)
        .toUpperCase()
    )
    .join('')
    .slice(0, 2) || '?';

const getId = (value: any): string => {
  if (typeof value === 'undefined' || value === null) return '';

  const nestedId = propz.get(value, ['_id']);
  if (typeof nestedId !== 'undefined' && nestedId !== null) return String(nestedId);
  if (typeof value.toHexString === 'function') return value.toHexString();

  const publicId = propz.get(value, ['id']);
  return typeof publicId !== 'undefined' && publicId !== null ? String(publicId) : String(value);
};

const getAudienceRoles = (user: any): string[] => {
  const permissionId = getId(propz.get(user, ['permissionId']));
  const permissions = propz.get(user, ['permissions'], []);
  const matchingPermissions = permissions.filter(permission => getId(permission) === permissionId);
  const roleText = getUserRole({
    ...user,
    permissions: matchingPermissions
  });

  return String(roleText || '')
    .split('\n')
    .map(role => role.trim())
    .filter(Boolean);
};

const groupAffectedUsers = (rawAffectedUsers: any[], hasChildVariables: boolean): any[] => {
  const groupedRecipients = new Map<string, any>();

  rawAffectedUsers.forEach((user, index) => {
    const userId = String(propz.get(user, ['userId'], propz.get(user, ['id'], index)));
    const childUserId = hasChildVariables ? String(propz.get(user, ['extra', 'childUserId'], '')) : '';
    const key = `${userId}:${childUserId}`;
    const audienceRoles = getAudienceRoles(user);
    const existing = groupedRecipients.get(key);

    if (existing) {
      audienceRoles.forEach(role => {
        if (existing.audienceRoles.indexOf(role) === -1) existing.audienceRoles.push(role);
      });
      return;
    }

    groupedRecipients.set(key, {
      ...user,
      audienceRoles
    });
  });

  return Array.from(groupedRecipients.values());
};

const CHANNEL_ICONS = {
  email: 'M4 4h16v16H4zM4 4l8 8 8-8',
  push: 'M18 8a6 6 0 1 0-12 0c0 4-2 5-2 5h16s-2-1-2-5M13.7 21a2 2 0 0 1-3.4 0',
  sms: 'M21 11.5a8.4 8.4 0 0 1-1 4L21 20l-4.3-1a8.4 8.4 0 1 1 4.3-7.5z'
};

const ChannelStatus: React.FC<{ active: boolean; icon: string; label: string }> = ({ active, icon, label }) => (
  <span
    className={`gmAffected__channel ${active ? 'gmAffected__channel--active' : ''}`}
    title={`${label}: ${active ? 'available' : 'not available'}`}
    aria-label={`${label}: ${active ? 'available' : 'not available'}`}
  >
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d={icon} />
    </svg>
  </span>
);

export const AffectedUserList: FunctionComponent<Props> = props => {
  const rawAffectedUsers = Array.isArray(props.affectedUsers) ? props.affectedUsers : [];
  const affectedUsers = useMemo(() => groupAffectedUsers(rawAffectedUsers, Boolean(props.hasChildVariables)), [
    rawAffectedUsers,
    props.hasChildVariables
  ]);
  const [activeChannelFilter, setActiveChannelFilter] = useState<RecipientChannelFilter>('all');
  const allUsers =
    affectedUsers.length > 0
      ? affectedUsers.length
      : typeof props.affectedUsersCount === 'number'
      ? props.affectedUsersCount
      : 0;
  const loadedChannelSummary = useMemo(
    () => ({
      total: affectedUsers.length,
      email: props.isEmailExist ? affectedUsers.filter(user => hasChannel(user, 'Email')).length : 0,
      push: props.isPushExist ? affectedUsers.filter(user => hasChannel(user, 'Push')).length : 0,
      sms: props.isSmsExist ? affectedUsers.filter(user => hasChannel(user, 'Sms')).length : 0,
      noChannels: affectedUsers.filter(
        user =>
          !(props.isEmailExist && hasChannel(user, 'Email')) &&
          !(props.isPushExist && hasChannel(user, 'Push')) &&
          !(props.isSmsExist && hasChannel(user, 'Sms'))
      ).length
    }),
    [affectedUsers, props.isEmailExist, props.isPushExist, props.isSmsExist]
  );
  const channelSummary =
    affectedUsers.length > 0 ? loadedChannelSummary : props.channelSummary || EMPTY_CHANNEL_SUMMARY;
  const isChannelSummaryAvailable = affectedUsers.length > 0 || typeof props.channelSummary !== 'undefined';
  const emailCount = props.isEmailExist ? channelSummary.email : 0;
  const pushCount = props.isPushExist ? channelSummary.push : 0;
  const smsCount = props.isSmsExist ? channelSummary.sms : 0;
  const noChannelsCount = channelSummary.noChannels;
  const filteredAffectedUsers = affectedUsers.filter(user => {
    switch (activeChannelFilter) {
      case 'email':
        return props.isEmailExist && hasChannel(user, 'Email');
      case 'push':
        return props.isPushExist && hasChannel(user, 'Push');
      case 'sms':
        return props.isSmsExist && hasChannel(user, 'Sms');
      case 'none':
        return (
          !(props.isEmailExist && hasChannel(user, 'Email')) &&
          !(props.isPushExist && hasChannel(user, 'Push')) &&
          !(props.isSmsExist && hasChannel(user, 'Sms'))
        );
      default:
        return true;
    }
  });
  const renderChannelMetric = (
    filter: RecipientChannelFilter,
    label: string,
    count: number,
    icon?: string
  ): React.ReactNode => {
    const content = (
      <>
        {icon && <ChannelStatus active={count > 0} icon={icon} label={label} />}
        {label} · {count}
      </>
    );

    if (affectedUsers.length === 0) {
      return filter === 'none' ? <em>{`${count} with no channels`}</em> : <span>{content}</span>;
    }

    return (
      <button
        type="button"
        className={`${filter === 'none' ? 'is-no-channels' : ''} ${activeChannelFilter === filter ? 'is-active' : ''}`}
        disabled={count === 0}
        aria-pressed={activeChannelFilter === filter}
        onClick={() => setActiveChannelFilter(activeChannelFilter === filter ? 'all' : filter)}
      >
        {filter === 'none' ? `${count} with no channels` : content}
      </button>
    );
  };

  if (!props.isModern) {
    return (
      <div className="gmAffected">
        <div className="gmAffected__head">
          <strong>{`Users affected (${allUsers})`}</strong>
          {props.isLoading && <span>Checking recipients...</span>}
          {props.isLoadingAffectedUserList && <span>Loading recipient list...</span>}
        </div>
        {affectedUsers.length === 0 && allUsers > 0 && (
          <div className="gmAffected__empty">
            <p>Recipient count has been calculated on the server. The full recipient list is not loaded yet.</p>
            {props.onLoadAffectedUsers && (
              <button type="button" className="gmButton gmButton--secondary" onClick={props.onLoadAffectedUsers}>
                Load recipient list
              </button>
            )}
          </div>
        )}
        {affectedUsers.length > 0 && (
          <>
            <div className="gmAffected__stats">
              {props.isEmailExist && <span>{`Email (${emailCount})`}</span>}
              {props.isPushExist && <span>{`Push (${pushCount})`}</span>}
              {props.isSmsExist && <span>{`SMS (${smsCount})`}</span>}
              <span>{`No channels (${noChannelsCount})`}</span>
            </div>
            <div className="gmAffected__tableWrap">
              <table className="gmAffected__table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Surname</th>
                    <th>Email</th>
                    <th>Roles</th>
                  </tr>
                </thead>
                <tbody>
                  {affectedUsers.map((user, index) => (
                    <tr key={`affected_user_list_row_${index}`}>
                      <td>{user.firstName}</td>
                      <td>{user.lastName}</td>
                      <td>{getEmailOrMisEmail(user)}</td>
                      <td>{getUserRole(user)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="gmRecipientsReview">
      <div className="gmAffected__header">
        <h2>Recipients</h2>
        {props.onHelpClick && (
          <div className="gmAffected__help">
            <HelpIconButton onClick={props.onHelpClick} label="Need help with this step?" />
          </div>
        )}
      </div>

      <div className="gmAffected__summary">
        {props.isLoading ? (
          <strong>Checking recipients…</strong>
        ) : affectedUsers.length > 0 ? (
          <button
            type="button"
            className={`gmAffected__totalMetric ${activeChannelFilter === 'all' ? 'is-active' : ''}`}
            aria-pressed={activeChannelFilter === 'all'}
            onClick={() => setActiveChannelFilter('all')}
          >
            {`${allUsers} ${allUsers === 1 ? 'user' : 'users'} affected`}
          </button>
        ) : (
          <strong>{`${allUsers} ${allUsers === 1 ? 'user' : 'users'} affected`}</strong>
        )}
        {!props.isLoading && isChannelSummaryAvailable && (
          <>
            <span className="gmAffected__summaryDivider" />
            <div className="gmAffected__summaryChannels">
              {props.isEmailExist && renderChannelMetric('email', 'Email', emailCount, CHANNEL_ICONS.email)}
              {props.isPushExist && renderChannelMetric('push', 'Push', pushCount, CHANNEL_ICONS.push)}
              {props.isSmsExist && renderChannelMetric('sms', 'SMS', smsCount, CHANNEL_ICONS.sms)}
              {renderChannelMetric('none', 'No channels', noChannelsCount)}
            </div>
          </>
        )}
        {!props.isLoading && affectedUsers.length === 0 && allUsers > 0 && props.onLoadAffectedUsers && (
          <button
            type="button"
            className="gmButton gmButton--secondary gmAffected__loadButton"
            disabled={props.isLoadingAffectedUserList}
            onClick={props.onLoadAffectedUsers}
          >
            {props.isLoadingAffectedUserList ? 'Loading recipients…' : 'View recipient list'}
          </button>
        )}
      </div>

      {!props.isLoading && !props.isLoadingAffectedUserList && allUsers === 0 && (
        <div className="gmAffected__emptyText">The message appears to be sent to 0 users.</div>
      )}

      {affectedUsers.length > 0 && (
        <>
          <div className="gmAffected__listMeta">
            <span>
              Showing {filteredAffectedUsers.length} of {affectedUsers.length}
            </span>
            {activeChannelFilter !== 'all' && (
              <button type="button" onClick={() => setActiveChannelFilter('all')}>
                Clear channel filter
              </button>
            )}
          </div>
          {filteredAffectedUsers.length === 0 ? (
            <div className="gmAffected__emptyText">No recipients match this channel filter.</div>
          ) : (
            <div className="gmAffected__tableWrap">
              <table className="gmAffected__table">
                <thead>
                  <tr>
                    <th>Recipient</th>
                    <th>Role</th>
                    {props.isEmailExist && <th className="gmAffected__channelCell">Email</th>}
                    {props.isPushExist && <th className="gmAffected__channelCell">Push</th>}
                    {props.isSmsExist && <th className="gmAffected__channelCell">SMS</th>}
                    <th>Delivery</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAffectedUsers.map((user, index) => {
                    const email = props.isEmailExist && hasChannel(user, 'Email');
                    const push = props.isPushExist && hasChannel(user, 'Push');
                    const sms = props.isSmsExist && hasChannel(user, 'Sms');
                    const canNotify = email || push || sms;
                    const avatar = propz.get(user, ['avatar'], '');

                    return (
                      <tr key={`${propz.get(user, ['userId'], propz.get(user, ['id'], 'recipient'))}_${index}`}>
                        <td>
                          <div className="gmAffected__recipient">
                            <span className="gmAffected__avatar">
                              {getInitials(user)}
                              {avatar && (
                                <img
                                  src={avatar}
                                  alt=""
                                  onError={event => (event.currentTarget.style.display = 'none')}
                                />
                              )}
                            </span>
                            <strong>{getName(user)}</strong>
                          </div>
                        </td>
                        <td>
                          <div className="gmAffected__roles">
                            {(propz.get(user, ['audienceRoles'], []) as string[]).length > 0 ? (
                              (propz.get(user, ['audienceRoles'], []) as string[]).map(role => (
                                <span key={role}>{role}</span>
                              ))
                            ) : (
                              <span className="is-unknown">Role unavailable</span>
                            )}
                          </div>
                        </td>
                        {props.isEmailExist && (
                          <td className="gmAffected__channelCell">
                            <ChannelStatus active={email} icon={CHANNEL_ICONS.email} label="Email" />
                          </td>
                        )}
                        {props.isPushExist && (
                          <td className="gmAffected__channelCell">
                            <ChannelStatus active={push} icon={CHANNEL_ICONS.push} label="Push" />
                          </td>
                        )}
                        {props.isSmsExist && (
                          <td className="gmAffected__channelCell">
                            <ChannelStatus active={sms} icon={CHANNEL_ICONS.sms} label="SMS" />
                          </td>
                        )}
                        <td>
                          <span
                            className={`gmAffected__delivery ${
                              canNotify ? 'gmAffected__delivery--success' : 'gmAffected__delivery--danger'
                            }`}
                          >
                            {canNotify ? 'Will be notified' : 'No channels available'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

AffectedUserList.displayName = 'AffectedUserList';
